import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

const ValueHierarchyContext = createContext({
  activePath: [],
  openNode: () => {},
  closeNode: () => {},
  focusNode: () => {},
});

export const normalizeValueNodePart = (value) => String(value ?? "")
  .replace(/[^a-zA-Z0-9_-]+/g, "-")
  .replace(/^-+|-+$/g, "") || "unknown";

export const buildValueNodeIdentity = (type, wbsCodeId) => {
  const safeType = normalizeValueNodePart(type);
  const safeWbs = normalizeValueNodePart(wbsCodeId);
  return {
    key: `${type}:${wbsCodeId}`,
    elementId: `value-node-${safeType}-${safeWbs}`,
    childrenId: `value-children-${safeType}-${safeWbs}`,
  };
};

export const ValueHierarchyProvider = ({ children, scopeKey }) => {
  const [activePath, setActivePath] = useState([]);

  useEffect(() => {
    setActivePath([]);
  }, [scopeKey]);

  const openNode = useCallback((nodeOrPath) => {
    const path = Array.isArray(nodeOrPath) ? nodeOrPath : [nodeOrPath];
    setActivePath(path.filter(Boolean));
  }, []);

  const closeNode = useCallback((nodeKey) => {
    setActivePath((current) => {
      const index = current.findIndex(({ key }) => key === nodeKey);
      return index < 0 ? current : current.slice(0, index);
    });
  }, []);

  const focusNode = useCallback((nodeKey) => {
    const node = activePath.find(({ key }) => key === nodeKey);
    if (!node) return;
    const target = document.getElementById(node.elementId);
    if (!target) return;
    target.focus({ preventScroll: true });
    target.scrollIntoView?.({ behavior: "smooth", block: "center" });
  }, [activePath]);

  const value = useMemo(() => ({ activePath, openNode, closeNode, focusNode }), [
    activePath,
    openNode,
    closeNode,
    focusNode,
  ]);

  return (
    <ValueHierarchyContext.Provider value={value}>
      {children}
    </ValueHierarchyContext.Provider>
  );
};

export const useValueHierarchy = () => useContext(ValueHierarchyContext);

export default ValueHierarchyContext;
